import type {AnnotationMapEntry} from 'mobx';

import {makeObservable, observable, runInAction} from 'mobx';

import type {BaseModelProps, ModelToProps as MTP} from './types';

import {not} from '../../../utils/conditionalUtils';

export abstract class BaseModel {
  /**
   * @deprecated Don't use this field. Will be removed in the future.
   */
  apiData?: any;
  id: string;

  constructor(props: BaseModelProps) {
    this.id = props.id;
    this.apiData = props.apiData;

    makeObservable(this, {
      apiData: observable.ref,
      id: observable,
      update: false,
    });
  }

  abstract get modelName(): string;

  static get modelName(): string {
    return 'BaseModel';
  }

  static mobxAnnotationFactory<
    T extends BaseModel,
    P extends BaseModel.ModelToProps<T, any>,
  >(props: P, overrides: Partial<Record<keyof T, AnnotationMapEntry>> = {}) {
    const defaultMap = new Map<keyof P, AnnotationMapEntry>();

    for (const key in props) {
      const value = props[key];

      switch (true) {
        case key === 'id':
        case key === 'apiData':
          break;
        case value instanceof BaseModel:
          defaultMap.set(key, observable.ref);
          break;
        case Array.isArray(value):
          defaultMap.set(key, observable.shallow);
          break;
        default:
          defaultMap.set(key, observable);
      }
    }

    return Object.assign(Object.fromEntries(defaultMap), overrides);
  }

  clone(deep = false): this {
    return this._clone(new WeakMap(), deep);
  }

  update(source: BaseModel.Source<typeof this>): this {
    const data = this.getUpdatableData(source);

    runInAction(() => {
      for (const key in data) {
        this.field(key, source[key]);
      }
    });

    return this;
  }

  private _clone(
    cloneCache: WeakMap<BaseModel, BaseModel>,
    deep: boolean,
  ): this {
    // If this instance has already been cloned, return the cached clone
    if (cloneCache.has(this)) {
      return cloneCache.get(this) as this;
    }

    const constructor = this.constructor as new (props: BaseModelProps) => this;

    // Create the clone first and cache it to break circular references
    const clone = new constructor(this);

    if (!deep) {
      return clone;
    }

    // Create a props object with all properties from the current instance
    const props: BaseModelProps & Record<string, any> = {
      apiData: this.apiData,
      id: this.id,
    };

    cloneCache.set(this, clone);

    // Copy all enumerable properties
    for (const key in this) {
      if (key === 'id' || key === 'apiData') continue; // Skip already handled properties

      const value = this[key as keyof this];

      if (value instanceof BaseModel) {
        props[key] = value._clone(cloneCache, true);
      } else if (Array.isArray(value)) {
        if (value.length === 0) {
          props[key] = [];
        } else {
          props[key] = value.map(item =>
            item instanceof BaseModel ? item._clone(cloneCache, true) : item,
          );
        }
      } else {
        props[key] = value;
      }
    }

    // Update the clone with all properties
    Object.assign(clone, props);

    return clone;
  }

  private field<K extends keyof typeof this>(
    field: K,
    value?: BaseModel.Source<typeof this>[K],
  ) {
    if (value !== undefined) {
      const target = this[field];
      const shouldCallTargetUpdate =
        target instanceof BaseModel && value !== null;

      if (shouldCallTargetUpdate) {
        target.update(value);
      } else {
        // @ts-expect-error: TS thinks that model[field] can be Partial<Model[K]>, but we checked it above
        this[field] = value;
      }
    }
  }

  private getUpdatableData(
    source: BaseModel.Source<typeof this>,
  ): BaseModel.Source<typeof this> {
    const data = Object.fromEntries(
      Object.entries(source).filter(([key]) => {
        const prevValue = this[key as keyof this];
        const nextValue = source[key as keyof this];
        const shouldSkip =
          typeof prevValue === 'function' || prevValue === nextValue;

        return not(shouldSkip);
      }),
    );

    return data as BaseModel.Source<typeof this>;
  }
}

export namespace BaseModel {
  export type ModelToProps<Model, OmitFields extends keyof Model = never> = MTP<
    Model,
    OmitFields
  >;

  export type Source<T> = {
    [P in keyof T]?: T[P] extends BaseModel ? Source<T[P]> : T[P];
  };
}
